/**
 * Element Extraction Component
 * Extract and filter elements by category from the Forge model
 */

import React, { useEffect, useState } from 'react';
import { useViewerStore } from '@/store/viewerStore';
import { useExtractionStore } from '@/store/extractionStore';
import { ExtractionService } from '@/services/core/extractionTools';
import { ViewerService } from '@/services/core/viewerTools';
import { ExtractedElementsList } from './ExtractedElementsList';
import { FamilySelector } from './FamilySelector';
import { ProgressBar } from '@/components/common/ProgressBar';
import { toast } from '@/components/common/Toast';

export function ElementExtraction() {
  const { viewer, isModelLoaded } = useViewerStore();
  const {
    categories,
    selectedCategory,
    selectedFamily,
    extractedElements,
    filteredElements,
    filterText,
    isExtracting,
    extractionProgress,
    extractionMode,
    setCategories,
    setSelectedCategory,
    setExtractedElements,
    setFilteredElements,
    setFilterText,
    setIsExtracting,
    setExtractionProgress,
    clearExtraction
  } = useExtractionStore();

  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load categories when model is loaded
  useEffect(() => {
    if (isModelLoaded && viewer && categories.length === 0) {
      loadCategories();
    }
  }, [isModelLoaded, viewer]);

  // Filter elements when filter text or extraction mode changes
  useEffect(() => {
    let filtered = extractedElements;
    
    // Apply family filter if in family mode
    if (extractionMode === 'family' && selectedFamily) {
      filtered = ExtractionService.filterElementsByFamily(extractedElements, selectedFamily);
    }
    
    // Apply text filter
    if (filterText) {
      filtered = filtered.filter(element => {
        const searchText = filterText.toLowerCase();
        return (
          element.name.toLowerCase().includes(searchText) ||
          element.family?.toLowerCase().includes(searchText) ||
          element.type?.toLowerCase().includes(searchText)
        );
      });
    }
    
    setFilteredElements(filtered);
    
    // Update viewer highlights based on filtered elements
    if (viewer && filtered.length > 0) {
      try {
        const dbIds = filtered.map(el => el.dbId).filter(id => id && id > 0);
        if (dbIds.length > 0) {
          ViewerService.highlightElements(viewer, dbIds);
        }
      } catch (err) {
      }
    }
  }, [filterText, extractedElements, extractionMode, selectedFamily, setFilteredElements, viewer]);

  const loadCategories = async () => {
    if (!viewer) return;

    setIsLoadingCategories(true);
    setError(null);

    try {
      const cats = await ExtractionService.getCategories(viewer);
      setCategories(cats);
      toast.success(`Loaded ${cats.length} categories`);
    } catch (err) {
      const errorMsg = 'Failed to load categories from model';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const handleExtract = async () => {
    if (!viewer || !selectedCategory) {
      const errorMsg = 'Please select a category first';
      setError(errorMsg);
      toast.warning(errorMsg);
      return;
    }

    setIsExtracting(true);
    setError(null);
    setExtractionProgress(0);

    try {
      // Always extract by category first
      const elements = await ExtractionService.extractByCategory(
        viewer,
        selectedCategory,
        (progress) => setExtractionProgress(progress)
      );
      
      setExtractedElements(elements);
      toast.success(`Extracted ${elements.length} elements from ${selectedCategory}`);

      // Highlight initially extracted elements in viewer
      if (elements.length > 0) {
        try {
          const dbIds = elements.map(el => el.dbId).filter(id => id && id > 0);
          if (dbIds.length > 0) {
            ViewerService.highlightElements(viewer, dbIds);
          }
        } catch (err) {
        }
      }
    } catch (err) {
      const errorMsg = 'Failed to extract elements';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsExtracting(false);
      setExtractionProgress(100);
    }
  };

  const handleClear = () => {
    clearExtraction();
    if (viewer) {
      ViewerService.clearHighlights(viewer);
    }
    setError(null);
    toast.info('Cleared extraction data');
  };

  const handleExport = () => {
    if (filteredElements.length === 0) {
      const errorMsg = 'No elements to export';
      setError(errorMsg);
      toast.warning(errorMsg);
      return;
    }

    ExtractionService.exportToJSON(filteredElements, selectedCategory);
    toast.success(`Exported ${filteredElements.length} elements to JSON`);
  };

  if (!isModelLoaded) {
    return (
      <div className="animate-fade-in p-4">
        <div className="text-center text-white">
          <p>Please load a model first to use element extraction</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-white">
        Element Extraction by Category
      </h2>

      {/* Status Bar */}
      <div className={`mb-4 p-3 rounded-lg border ${
        error 
          ? 'bg-red-900/20 border-red-800' 
          : isExtracting
          ? 'bg-yellow-900/20 border-yellow-800'
          : extractedElements.length > 0
          ? 'bg-green-900/20 border-green-800'
          : 'bg-dark-200 border-gray-700'
      }`}>
        <div className="flex items-center justify-between">
          <span className="text-sm">
            {error ? (
              <span className="text-red-400">Error: {error}</span>
            ) : isExtracting ? (
              <span className="text-yellow-400">Extracting elements... {extractionProgress.toFixed(0)}%</span>
            ) : extractedElements.length > 0 ? (
              <span className="text-green-400">
                Extracted {filteredElements.length} of {extractedElements.length} elements
              </span>
            ) : (
              <span className="text-white">Select a category to extract elements</span>
            )}
          </span>
        </div>
      </div>

      {/* Category Selection */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-white mb-2">
          Select Category
        </label>
        <select
          className="input-dark w-full"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          disabled={isLoadingCategories || isExtracting}
        >
          <option value="">
            {isLoadingCategories ? 'Loading categories...' : 'Select a category...'}
          </option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <p className="text-xs text-white mt-1">
          Choose a category to extract elements
        </p>
      </div>

      {/* Extraction Progress */}
      {isExtracting && (
        <div className="mb-4">
          <ProgressBar 
            progress={extractionProgress}
            label="Extracting elements..."
            color="primary"
            striped
            animated
          />
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 mb-6">
        <button
          className="btn btn-primary flex-1"
          onClick={handleExtract}
          disabled={!selectedCategory || isExtracting}
        >
          Extract Elements
        </button>
        <button
          className="btn btn-secondary flex-1"
          onClick={handleClear}
          disabled={isExtracting}
        >
          Clear
        </button>
        {extractedElements.length > 0 && (
          <button
            className="btn btn-success flex-1"
            onClick={handleExport}
          >
            <span>📥</span> Export to JSON
          </button>
        )}
      </div>

      {/* Family Selector - Shows after extraction */}
      {extractedElements.length > 0 && (
        <div className="mb-4">
          <FamilySelector />
        </div>
      )}

      {/* Filter Input */}
      {extractedElements.length > 0 && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-white mb-2">
            Search Elements
          </label>
          <input
            type="text"
            className="input-dark w-full"
            placeholder="Type to filter elements by name, family, or type..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          <p className="text-xs text-white mt-1">
            Filter uses partial matching - type any part of the element name
          </p>
        </div>
      )}

      {/* Extracted Elements List */}
      {extractedElements.length > 0 && (
        <ExtractedElementsList elements={filteredElements} />
      )}
    </div>
  );
}